/**
 * Pure rules behind the "Edit publishing" dialog: how a set of brand items is
 * published across storefronts, and which storefront changes an edit turns into.
 */

/** How the selected items as a whole sit in one storefront. */
export enum ShopPublishState {
   Published = 'published',
   Unpublished = 'unpublished',
   /** Some selected items are published there, some are not. */
   Mixed = 'mixed',
}

/** The slice of a brand item the publishing rules need. */
export type PublishItem = {
   _id: string
   shops?: ({ shopId?: string | null, active?: boolean | null } | null)[] | null
}

/** The publishing state wanted in one storefront, as BrandItemsPublish takes it. */
export type PublishShopChange = {
   shopId: string
   active: boolean
}

/** Whether the item is currently published in the storefront. */
export const isPublishedIn = (item: PublishItem, shopId: string): boolean => {
   return !!item.shops?.some(shop => String(shop?.shopId) === String(shopId) && shop?.active);
}

/** Summarises the items' publishing in one storefront - Mixed when they disagree. */
export const shopPublishState = (items: PublishItem[], shopId: string): ShopPublishState => {
   const published = items.filter(item => isPublishedIn(item, shopId)).length;
   if (published === 0) {
      return ShopPublishState.Unpublished;
   }
   return published === items.length ? ShopPublishState.Published : ShopPublishState.Mixed;
}

/**
 * The state a checkbox moves to when clicked. Mixed is only reachable again when
 * the storefront started out mixed, so the user can back out to "leave as is".
 */
export const nextPublishState = (current: ShopPublishState, initial: ShopPublishState): ShopPublishState => {
   if (current === ShopPublishState.Published) {
      return ShopPublishState.Unpublished;
   }
   if (current === ShopPublishState.Unpublished && initial === ShopPublishState.Mixed) {
      return ShopPublishState.Mixed;
   }
   return ShopPublishState.Published;
}

/**
 * The storefronts the user actually changed, ready to send to BrandItemsPublish.
 * Mixed storefronts mean "leave as is" and are never sent; Core skips items
 * that are already in the requested state.
 */
export const changedShops = (initial: Record<string, ShopPublishState>, desired: Record<string, ShopPublishState>): PublishShopChange[] => {
   return Object.entries(desired)
      .filter(([shopId, state]) => state !== ShopPublishState.Mixed && state !== initial[shopId])
      .map(([shopId, state]) => ({shopId, active: state === ShopPublishState.Published}));
}
